import { useQuery } from '@tanstack/react-query';
import type { Player, Translated } from '@uefadigital/panenka';
import { PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { ChangeEvent } from 'react';
import { memo, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import type { PlayerWithTeam } from './types';

import { getPlayersStatsQuery } from '@queries/statistics';
import { getPlayerUrl } from '@services/LinkRuleService';
import { hydrateWithTranslations } from '@services/TranslatorService';
import PlayerIdentifier from '@shared/Identifier/PlayerIdentifier';
import { windowized } from '@shared/Windowizer';
import type { Enum } from '@shared-types/enum.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { getStatNumericValue } from '@utilities/stats';
import { removeDiacritics } from '@utilities/text-manipulation';

import * as classes from './styles.module.scss';

const WindowizedPlayerIdentifier = memo(windowized(PlayerIdentifier));

export const SearchType = {
  DROPDOWN: 'dropdown',
  FULLSCREEN: 'fullscreen',
} as const;
export type SearchType = Enum<typeof SearchType>;

const activationThreshold = 2;
const PlayerSearch = ({
  options,
  onPlayersLoad,
  onItemClick,
}: {
  options: any;
  onPlayersLoad?: (players: Translated<PlayerWithTeam>[]) => unknown;
  onItemClick?: (players: Translated<Player>) => unknown;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState<Translated<Player>>(null);

  const { data: loadedPlayers } = useQuery({
    ...getPlayersStatsQuery({
      competitionId: options.cup,
      seasonYear: options.seasonYear ?? 'current',
      phase: options.phase ?? 'TOURNAMENT',
      limit: options.limit ?? 100,
      stats: ['matches_appearance'],
    }),
    placeholderData: [],
  });

  const players = useMemo(
    () =>
      loadedPlayers
        .map((s) =>
          hydrateWithTranslations({
            ...s.player,
            team: s.team,
            matches: getStatNumericValue(s, { name: 'matches_appearance' }),
          }),
        )
        .toSorted((prev, next) => (prev.shortName < next.shortName ? -1 : 1)),
    [loadedPlayers],
  );

  useEffect(() => {
    if (onPlayersLoad && players?.length) {
      onPlayersLoad(players);
    }
  }, [onPlayersLoad, players]);

  const searchTextDeferred = useDeferredValue(searchText);
  const filteredPlayers = useMemo(
    () =>
      players.filter(
        (player) =>
          (options.type === SearchType.FULLSCREEN && !searchTextDeferred) ||
          (searchTextDeferred && searchTextDeferred.length < activationThreshold) ||
          removeDiacritics(player.shortName.toLowerCase()).includes(
            removeDiacritics(searchTextDeferred.toLowerCase()),
          ) ||
          removeDiacritics(hydrateWithTranslations(player.team)?.displayName.toLowerCase()).includes(
            removeDiacritics(searchTextDeferred.toLowerCase()),
          ),
      ),
    [options.type, players, searchTextDeferred],
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setSelectedPlayer(null);

    options.type === SearchType.DROPDOWN && onItemClick?.({ id: null });
  };

  const handleBlur = () => {
    if (options.type === SearchType.DROPDOWN) {
      setSearchText('');
    }
  };

  const goToPlayerPage = (player: Player) => {
    GoogleTagManagerHelper.ga4Event('history_click', { interaction: 'Select player in autocomplete' });
    const url = getPlayerUrl(player.id, options.cup);
    window.location.href = url;
  };

  const handlePlayerClick = (player: Translated<Player>) => {
    if (options.type === SearchType.DROPDOWN) {
      setSelectedPlayer(player);
    }

    onItemClick(player);
  };

  const handleItemClick = (player: Translated<Player>) => () => {
    setSearchText('');
    return onItemClick ? handlePlayerClick(player) : goToPlayerPage(player);
  };

  const intersectionOptions = useMemo(
    () => ({
      root: scrollRef.current,
      rootMargin: '200px 0px',
    }),
    [],
  );

  return (
    <div
      className={clsx({
        [classes.wrapper]: true,
        [classes.dropdown]: options.type !== 'fullscreen',
        [classes.fullscreen]: options.type === 'fullscreen',
      })}
    >
      <div className={classes.search}>
        <div className={classes.searchWrapper}>
          <PkIcon className={classes.iconSearch} color='interaction' name='actions-search' />
          {selectedPlayer && options.type === SearchType.DROPDOWN && (
            <img
              src={selectedPlayer.mediumLogoUrl}
              className='club-logo'
              title={selectedPlayer.shortName}
              alt={selectedPlayer.shortName}
            />
          )}
          <input
            type='text'
            className={classes.input}
            placeholder={options.placeholder}
            onChange={handleSearch}
            onBlur={handleBlur}
            value={selectedPlayer ? selectedPlayer.shortName : searchText}
          />
        </div>
      </div>

      {!selectedPlayer && (
        <div className={classes.list} ref={scrollRef}>
          {filteredPlayers.map((player) => (
            <WindowizedPlayerIdentifier
              windowizeClassName={classes.placeholder}
              intersectionOptions={intersectionOptions}
              key={player.id}
              player={player}
              className={clsx(classes.identifier, 'pk-py--xs')}
              type={options.type === 'fullscreen' ? 'vertical' : 'horizontal'}
              customSecondaryText={hydrateWithTranslations(player.team)?.displayName}
              showPosition={false}
              selected={selectedPlayer?.id === player.id}
              enableSelection
              onIdentifierSelected={handleItemClick(player)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(PlayerSearch);
